/**
 * escalation-detail-page.tsx — Escalation detail view with claim + resolve actions.
 *
 * Story 8.2: Task 7.2
 *
 * AC 5: Claim button only active if unclaimed; shows claimer name if claimed by other.
 * AC 8: Resolve form with required resolution note; only active if claimed by current user.
 */

import { usePortalAuth } from '@nrx/auth-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

// @nrx/auth-ui is not built in worktrees — cast to known shape to avoid no-unsafe-* errors.
interface PortalAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { access_token: string; profile: { sub: string } } | null;
}

import type { EscalationQueueEntry } from './escalation-api.js';
import { escalationApi } from './escalation-api.js';

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString();
}

function formatSlaRemaining(seconds: number): string {
  if (seconds < 0) return 'SLA BREACHED';
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m remaining`;
  return `${String(Math.floor(seconds / 3600))}h remaining`;
}

/**
 * Generate an idempotency key that is stable for the lifecycle of a single escalation action.
 * Fix (finding #7): key is generated ONCE per component mount for a given escalation, not inside
 * each handler call. This ensures double-click / StrictMode double-invoke sends the same key,
 * so the 24-hr server-side cache actually deduplicates concurrent requests.
 *
 * Key format: "<escalationId>-<actionType>-<uuid>" — uniquely scoped to this page visit.
 */
function generateActionIdempotencyKey(escalationId: string, action: 'claim' | 'resolve'): string {
  return `${escalationId}-${action}-${crypto.randomUUID()}`;
}

export function EscalationDetailPage(): React.ReactElement {
  const { escalationId } = useParams<{ escalationId: string }>();
  const navigate = useNavigate();

  const auth = usePortalAuth() as PortalAuthResult;
  const token = auth.user?.access_token;
  const currentStaffId = auth.user?.profile?.sub;

  const [escalation, setEscalation] = useState<EscalationQueueEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  // Fix (finding #7): stable idempotency keys — generated once per page mount,
  // not inside each handler. This means StrictMode double-invokes and double-clicks
  // send the same key and hit the 24-hr server-side dedup cache.
  // escalationId is required by the route; handlers guard against undefined before use.
  const claimIdempotencyKey = React.useRef<string>(
    generateActionIdempotencyKey(escalationId ?? '', 'claim'),
  );
  const resolveIdempotencyKey = React.useRef<string>(
    generateActionIdempotencyKey(escalationId ?? '', 'resolve'),
  );

  const fetchDetail = useCallback(async (): Promise<void> => {
    if (!escalationId) return;
    try {
      const detail = await escalationApi.getEscalation(escalationId, token);
      setEscalation(detail);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load escalation.');
    } finally {
      setIsLoading(false);
    }
  }, [escalationId, token]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  async function handleClaim(): Promise<void> {
    if (!escalationId) return;
    setIsClaiming(true);
    setActionError(null);
    try {
      // Fix (finding #6): claim endpoint returns bare Escalation, not enriched EscalationQueueEntry.
      // Always refetch the enriched detail after claim to avoid rendering NaN SLA / missing member name.
      // Fix (finding #7): use stable claimIdempotencyKey.current so retries / double-click hit cache.
      await escalationApi.claimEscalation(escalationId, claimIdempotencyKey.current, token);
      // Refetch enriched detail (slaRemainingSeconds, memberName, conversationHistory etc.)
      await fetchDetail();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to claim escalation.');
    } finally {
      setIsClaiming(false);
    }
  }

  async function handleResolve(): Promise<void> {
    if (!escalationId || !resolutionNote.trim()) return;
    setIsResolving(true);
    setActionError(null);
    try {
      // Fix (finding #6): same — resolveEscalation returns bare Escalation; use stable key.
      // Fix (finding #7): use stable resolveIdempotencyKey.current.
      await escalationApi.resolveEscalation(
        escalationId,
        resolutionNote.trim(),
        resolveIdempotencyKey.current,
        token,
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      navigate('/escalation-queue', {
        state: { successMessage: 'Escalation resolved successfully.' },
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to resolve escalation.');
      setIsResolving(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-500">Loading escalation detail…</p>
      </div>
    );
  }

  if (error || !escalation) {
    return (
      <div className="p-6">
        <div className="rounded bg-red-50 p-4 text-red-700" role="alert">
          {error ?? 'Escalation not found.'}
        </div>
      </div>
    );
  }

  const isClaimedByCurrentUser = escalation.claimedBy?.staffId === currentStaffId;
  const isClaimedByOther = escalation.claimedBy !== null && !isClaimedByCurrentUser;
  const canClaim = escalation.status === 'open';
  const canResolve =
    escalation.status === 'claimed' && isClaimedByCurrentUser && resolutionNote.trim().length > 0;

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/escalation-queue')}
          className="text-sm text-blue-600 hover:underline"
          type="button"
        >
          ← Back to queue
        </button>
        <span
          className={`rounded px-3 py-1 text-sm font-medium ${
            escalation.severity === 'emergency'
              ? 'bg-red-100 text-red-700'
              : escalation.severity === 'high'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-gray-100 text-gray-700'
          }`}
          data-testid="severity-badge"
        >
          {escalation.severity.toUpperCase()}
        </span>
      </div>

      {/* Trigger details */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Trigger Details</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-gray-500">Trigger type</dt>
            <dd className="font-medium">{escalation.triggerType}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Source</dt>
            <dd className="font-medium">{escalation.triggerSource}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Triggered at</dt>
            <dd className="font-medium">{formatDateTime(escalation.triggerTimestamp)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">SLA</dt>
            <dd
              className={`font-medium ${
                escalation.slaRemainingSeconds < 0 ? 'text-red-600' : 'text-gray-900'
              }`}
            >
              {formatSlaRemaining(escalation.slaRemainingSeconds)}
            </dd>
          </div>
          {escalation.triggerReason && (
            <div className="col-span-2">
              <dt className="text-gray-500">Reason</dt>
              <dd className="font-medium">{escalation.triggerReason}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Member context */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Member Context</h2>
        {escalation.memberName ? (
          <p className="mb-2 font-medium text-gray-900">{escalation.memberName}</p>
        ) : (
          <p className="text-gray-400 italic text-sm">Member context unavailable</p>
        )}
        {escalation.memberBackground && (
          <p className="mt-2 text-sm text-gray-600">{escalation.memberBackground}</p>
        )}
        {escalation.conversationHistory && (
          <div className="mt-3 rounded bg-gray-50 p-3">
            <h3 className="mb-1 text-xs font-semibold text-gray-500 uppercase">
              Recent Conversation
            </h3>
            <pre className="whitespace-pre-wrap text-xs text-gray-700">
              {escalation.conversationHistory}
            </pre>
          </div>
        )}
      </div>

      {/* Claim/resolve actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="mb-3 text-lg font-semibold text-gray-900">Actions</h2>

        {actionError && (
          <div className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700" role="alert">
            {actionError}
          </div>
        )}

        {/* Claim status */}
        {escalation.status === 'resolved' && (
          <div className="rounded bg-green-50 p-3 text-sm text-green-700">
            Resolved by {escalation.resolvedBy?.staffRole} at{' '}
            {escalation.resolvedBy ? formatDateTime(escalation.resolvedBy.resolvedAt) : '—'}
            <p className="mt-1 text-xs text-green-600">
              Note: {escalation.resolvedBy?.resolutionNote}
            </p>
          </div>
        )}

        {escalation.status === 'open' && (
          <button
            onClick={() => void handleClaim()}
            disabled={isClaiming || !canClaim}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            data-testid="claim-button"
            type="button"
          >
            {isClaiming ? 'Claiming…' : 'Claim Escalation'}
          </button>
        )}

        {isClaimedByOther && (
          <div
            className="rounded bg-blue-50 p-3 text-sm text-blue-700"
            data-testid="claimed-by-other"
          >
            This escalation is claimed by <strong>{escalation.claimedBy!.staffRole}</strong>{' '}
            (read-only).
          </div>
        )}

        {isClaimedByCurrentUser && escalation.status === 'claimed' && (
          <div className="mt-4">
            <label htmlFor="resolution-note" className="block text-sm font-medium text-gray-700">
              Resolution Note <span className="text-red-500">*</span>
            </label>
            <textarea
              id="resolution-note"
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              rows={4}
              placeholder="Enter your clinical resolution note…"
              className="mt-1 w-full rounded border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none"
              data-testid="resolution-note-input"
            />
            <button
              onClick={() => void handleResolve()}
              disabled={isResolving || !canResolve}
              className="mt-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              data-testid="resolve-button"
              type="button"
            >
              {isResolving ? 'Resolving…' : 'Mark Resolved'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
